import * as yup from "yup";

/**
 * Shared constants and validation utilities for chamber member forms
 * (CreateChamberMember, EditChamberMember, ManageAChamberMember, etc.)
 */

/** Maximum character count for the company description field */
export const CHARACTER_LIMIT_DESCRIPTION = 2000;

/**
 * Selects and normalizes the dashboard login email for a create-member request.
 * A server-suggested plus address must take priority over Formik's current value
 * because Formik field updates are not synchronous.
 */
export const resolveCreateMemberAdminConsoleLoginEmail = ({
  enteredEmail,
  suggestedEmailOverride,
}: {
  enteredEmail: string;
  suggestedEmailOverride?: string | null;
}): string =>
  (suggestedEmailOverride?.trim() || enteredEmail.trim()).toLowerCase();

/**
 * A status update is successful only when the mutation returns the requested
 * Boolean value. Missing or stale mutation data must not produce a success UI.
 */
export const chamberMemberSuspensionUpdateMatchesRequest = ({
  requestedIsSuspended,
  returnedIsSuspended,
}: {
  requestedIsSuspended: boolean;
  returnedIsSuspended: boolean | null | undefined;
}): boolean => returnedIsSuspended === requestedIsSuspended;

/**
 * Member form data can be applied only once, after both independent member
 * reads finish. This prevents a late email response from replacing user edits.
 */
export const shouldInitializeChamberMemberForm = ({
  chamberMemberId,
  initializedMemberId,
  memberLoading,
  adminConsoleLoginEmailLoading,
  hasMemberData,
}: {
  chamberMemberId?: string;
  initializedMemberId: string | null;
  memberLoading: boolean;
  adminConsoleLoginEmailLoading: boolean;
  hasMemberData: boolean;
}): boolean =>
  Boolean(chamberMemberId) &&
  !memberLoading &&
  !adminConsoleLoginEmailLoading &&
  hasMemberData &&
  initializedMemberId !== chamberMemberId;

/**
 * Reusable Yup address validation schema object.
 * All address fields are optional strings.
 */
export const addressValidationSchema = yup.object({
  province: yup.string().required("Province/State is required"),
  city: yup.string().required("City is required"),
  postalCode: yup.string(),
  streetAddress: yup.string(),
});

/**
 * Scrolls to the first validation error visible on the page.
 * Looks for MUI error classes and scrolls the enclosing form control into view.
 *
 * @param formikSubmitCount - The current formik submitCount value
 * @param formikErrors - The current formik errors object
 */
export const scrollToFirstValidationError = (
  formikSubmitCount: number,
  formikErrors: Record<string, unknown>
): void => {
  if (formikSubmitCount > 0 && Object.keys(formikErrors).length > 0) {
    const firstErrorField = document.querySelector(
      '.Mui-error, .MuiFormHelperText-root.Mui-error'
    );
    if (firstErrorField) {
      const container =
        firstErrorField.closest('.MuiFormControl-root') || firstErrorField;
      container.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }
};
